import React, { useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import {
	Bar,
	CartesianGrid,
	ComposedChart,
	Label,
	LabelList,
	Legend,
	Line,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import SVGChart from "components/_common/icons/vdm/SVGChart";
import SVGLineChart from "components/_common/icons/vdm/SVGLineChart";
import SVGDownload from "components/_common/icons/vdm/SVGDownload";

interface IChartData {
	date: string;
	consumption: number;
	tankPressure?: number;
	vapourTemp?: number;
	liquidTemp?: number;
}

interface IProcessedChartData extends IChartData {
	consumptionBase: number;
	consumptionOver: number;
}

const chartData: IChartData[] = [
	{
		date: "01 Jan",
		consumption: 20,
		tankPressure: 0.73,
		vapourTemp: 119.5,
		liquidTemp: 112.5,
	},
	{
		date: "02 Jan",
		consumption: 22,
		tankPressure: 0.75,
		vapourTemp: 118.0,
		liquidTemp: 115.3,
	},
	{
		date: "03 Jan",
		consumption: 17,
		tankPressure: 0.74,
		vapourTemp: 118.0,
		liquidTemp: 115.3,
	},
	{
		date: "04 Jan",
		consumption: 21,
		tankPressure: 0.74,
		vapourTemp: 118.5,
		liquidTemp: 118.0,
	},
	{
		date: "05 Jan",
		consumption: 25,
		tankPressure: 0.71,
		vapourTemp: 118.0,
		liquidTemp: 121.0,
	},
	{
		date: "06 Jan",
		consumption: 17,
		tankPressure: 0.67,
		vapourTemp: 119.2,
		liquidTemp: 119.2,
	},
	{
		date: "07 Jan",
		consumption: 18,
		tankPressure: 0.64,
		vapourTemp: 121.5,
		liquidTemp: 119.0,
	},
	{
		date: "08 Jan",
		consumption: 14,
		tankPressure: 0.57,
		vapourTemp: 121.8,
		liquidTemp: 121.0,
	},
	{
		date: "09 Jan",
		consumption: 17,
		tankPressure: 0.54,
		vapourTemp: 124.0,
		liquidTemp: 119.0,
	},
	{
		date: "10 Jan",
		consumption: 15,
		tankPressure: 0.49,
		vapourTemp: 123.5,
		liquidTemp: 121.0,
	},
	{
		date: "11 Jan",
		consumption: 13,
		tankPressure: 0.45,
		vapourTemp: 121.5,
		liquidTemp: 119.5,
	},
	{
		date: "12 Jan",
		consumption: 15,
		tankPressure: 0.43,
		vapourTemp: 115.0,
		liquidTemp: 110.0,
	},
	{
		date: "13 Jan",
		consumption: 11,
		tankPressure: 0.58,
		vapourTemp: 110.0,
		liquidTemp: 109.5,
	},
	{
		date: "14 Jan",
		consumption: 9,
		tankPressure: 0.59,
		vapourTemp: 109.5,
		liquidTemp: 107.0,
	},
	{
		date: "15 Jan",
		consumption: 13,
		tankPressure: 0.71,
		vapourTemp: 107.0,
		liquidTemp: 109.5,
	},
	{
		date: "16 Jan",
		consumption: 16,
		tankPressure: 0.55,
		vapourTemp: 122.0,
		liquidTemp: 108.5,
	},
	{
		date: "17 Jan",
		consumption: 14,
		tankPressure: 0.5,
		vapourTemp: 109.5,
		liquidTemp: 122.0,
	},
	{
		date: "18 Jan",
		consumption: 19,
		tankPressure: 0.57,
		vapourTemp: 111.5,
		liquidTemp: 110.8,
	},
	{
		date: "19 Jan",
		consumption: 17,
		tankPressure: 0.66,
		vapourTemp: 110.8,
		liquidTemp: 111.5,
	},
	{
		date: "20 Jan",
		consumption: 10,
		tankPressure: 0.72,
		vapourTemp: 104.5,
		liquidTemp: 104.0,
	},
	{
		date: "21 Jan",
		consumption: 13,
		tankPressure: 0.7,
		vapourTemp: 104.0,
		liquidTemp: 104.5,
	},
	{
		date: "22 Jan",
		consumption: 15,
		tankPressure: 0.73,
		vapourTemp: 109.0,
		liquidTemp: 106.0,
	},
	{
		date: "23 Jan",
		consumption: 22,
		tankPressure: 0.7,
		vapourTemp: 110.0,
		liquidTemp: 109.0,
	},
	{
		date: "24 Jan",
		consumption: 28,
		tankPressure: 0.69,
		vapourTemp: 113.0,
		liquidTemp: 110.0,
	},
	{
		date: "25 Jan",
		consumption: 78,
		tankPressure: 0.7,
		vapourTemp: 115.0,
		liquidTemp: 113.0,
	},
	{
		date: "26 Jan",
		consumption: 29,
		tankPressure: 0.69,
		vapourTemp: 122.5,
		liquidTemp: 115.0,
	},
	{
		date: "27 Jan",
		consumption: 28,
		tankPressure: 0.69,
		vapourTemp: 124.5,
		liquidTemp: 122.5,
	},
	{
		date: "28 Jan",
		consumption: 27,
		tankPressure: 0.69,
		vapourTemp: 125.0,
		liquidTemp: 124.5,
	},
	{
		date: "29 Jan",
		consumption: 26,
		tankPressure: 0.69,
		vapourTemp: 124.0,
		liquidTemp: 125.0,
	},
	{
		date: "30 Jan",
		consumption: 24,
		tankPressure: 0.68,
		vapourTemp: 123.0,
		liquidTemp: 124.0,
	},
	{
		date: "31 Jan",
		consumption: 23,
		tankPressure: 0.66,
		vapourTemp: 123.0,
		liquidTemp: 123.0,
	},
];

interface CustomTooltipProps {
	active?: boolean;
	payload?: any[];
	label?: string;
}

const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
	if (active && payload && payload.length) {
		const data = payload[0].payload;
		const totalConsumption = data.consumption;
		const otherItems = payload.filter(
			(p) => p.dataKey !== "consumptionBase" && p.dataKey !== "consumptionOver"
		);

		return (
			<div className="bg-white/90 p-3 rounded-lg border border-gray-200 shadow-md text-sm">
				<p className="font-medium text-gray-800">{label}</p>
				<ul className="mt-1 space-y-1">
					<li
						style={{
							color:
								series.find((s) => s.dataKey === "consumption")?.color ||
								"#000",
						}}
					>
						{`Consumption: ${totalConsumption.toFixed(2)}`}
					</li>
					{otherItems.map((p, i) => (
						<li key={i} style={{ color: p.color }}>
							{`${p.name}: ${
								typeof p.value === "number" ? p.value.toFixed(2) : p.value
							}`}
						</li>
					))}
				</ul>
			</div>
		);
	}
	return null;
};

const iconMap: Record<string, React.ReactNode> = {
	consumption: <SVGChart className="text-[#6172F3]" />,
	tankPressure: <SVGLineChart className="text-[#667085]" />,
	vapourTemp: <SVGLineChart className="text-[#FAC515]" />,
	liquidTemp: <SVGLineChart className="text-[#36BFFA]" />,
};
interface ICustomIconLegendProps {
	visibility: TVisibilityState;
	toggleSeries: (dataKey: keyof TVisibilityState) => void;
}

const CustomIconLegend = ({
	visibility,
	toggleSeries,
}: ICustomIconLegendProps) => {
	return (
		<div className="flex items-center justify-end space-x-4">
			{series.map((s) => (
				<button
					key={s.name}
					onClick={() => toggleSeries(s.dataKey as keyof TVisibilityState)}
					className={`transition-opacity flex items-center gap-x-4 duration-200 ${
						!visibility[s.dataKey as keyof TVisibilityState]
							? "opacity-30"
							: "opacity-100"
					}`}
					title={s.name}
				>
					<span>{iconMap[s.dataKey as string]}</span>
					<span>{s.dataKey}</span>
				</button>
			))}
		</div>
	);
};

const series = [
	{ name: "Consumption", dataKey: "consumption", color: "#6172F3" },
	{ name: "Tank Pressure", dataKey: "tankPressure", color: "#667085" },
	{ name: "Vapour Temp.", dataKey: "vapourTemp", color: "#FAC515" },
	{ name: "Liquid Temp.", dataKey: "liquidTemp", color: "#36BFFA" },
];

type TVisibilityState = Record<keyof Omit<IChartData, "date">, boolean>;

type OwnProps = {};
type LNGConsumptionChartProps = PropsFromRedux & OwnProps;
function LNGConsumptionChart(props: LNGConsumptionChartProps) {
	const [visibility, setVisibility] = useState<TVisibilityState>({
		consumption: true,
		tankPressure: true,
		vapourTemp: true,
		liquidTemp: true,
	});

	const limit = 64;
	const processedData: IProcessedChartData[] = chartData.map((d) => ({
		...d,
		consumptionBase: Math.min(d.consumption, limit),
		consumptionOver: d.consumption > limit ? d.consumption - limit : 0,
	}));

	const toggleMetrics = (dataKey: keyof TVisibilityState) => {
		setVisibility((prev) => ({ ...prev, [dataKey]: !prev[dataKey] }));
	};
	return (
		<div className="bg-white p-4 shadow-md rounded-md border border-gray-300 mt-4">
			<div className="flex items-center justify-between rounded mb-4">
				<h3 className="text-xl text-gray-700 font-medium">LNG Consumption</h3>
				<div className="p-2 rounded border border-gray-300">
					<SVGDownload />
				</div>
			</div>
			<div style={{ width: "100%", height: 450 }}>
				<ResponsiveContainer>
					<ComposedChart
						data={processedData}
						margin={{ top: 30, right: 30, left: 20, bottom: 20 }}
					>
						<CartesianGrid vertical={true} stroke="#e5e7eb" horizontal={true}  />
						<XAxis
							dataKey="date"
							tickLine={false}
							tick={{ fontSize: 12, fill: "#6b7280" }}
							dy={10}
							interval={1}
							axisLine={{ stroke: "#e5e7eb", strokeDasharray: "3 3" }}
						/>
						<YAxis
							yAxisId="consumption"
							orientation="left"
							tickLine={false}
							label={{
								angle: -90,
								value: "Consumption",
							}}
							tick={{ fontSize: 12, fill: "#6b7280" }}
							domain={[0, 80]}
							ticks={[0, 16, 24, 48, 64, 80]}
							axisLine={{ stroke: "#e5e7eb", strokeDasharray: "3 3" }}
						/>
						<YAxis yAxisId="pressure" hide domain={[0.4, 0.8]} />
						<YAxis yAxisId="temp" hide domain={[100, 130]} />

						<Tooltip
							cursor={{
								stroke: "#d1d5db",
								strokeWidth: 1,
								strokeDasharray: "3 3",
							}}
							content={<CustomTooltip />}
						/>

						<Legend
							verticalAlign="top"
							align="right"
							wrapperStyle={{ top: 0, right: 0 }}
							content={
								<CustomIconLegend
									visibility={visibility}
									toggleSeries={toggleMetrics}
								/>
							}
						/>

						<ReferenceLine
							y={64}
							yAxisId="consumption"
							stroke="#ef4444"
							strokeDasharray="3 3"
						>
							<Label
								value="Limit"
								position="left"
								fill="#ef4444"
								fontSize={12}
								dx={-35}
								dy={-2}
							/>
						</ReferenceLine>

						{visibility.consumption && (
							<>
								<Bar
									dataKey="consumptionBase"
									stackId="a"
									yAxisId="consumption"
									fill={series[0].color}
									barSize={20}
								/>
								<Bar
									dataKey="consumptionOver"
									stackId="a"
									yAxisId="consumption"
									fill="#F97066"
									barSize={20}
									radius={[4, 4, 0, 0]}
								/>
							</>
						)}
						{visibility.tankPressure && (
							<Line
								type="monotone"
								dataKey="tankPressure"
								yAxisId="pressure"
								stroke={series[1].color}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 3, strokeWidth: 2, stroke: series[1].color }}
							>
								<LabelList
									dataKey="tankPressure"
									position="top"
									offset={8}
									style={{
										fontSize: 10,
										fill: series[1].color,
										fontWeight: 500,
									}}
									formatter={(value: number) => (value ? value.toFixed(2) : "")}
								/>
							</Line>
						)}
						{visibility.vapourTemp && (
							<Line
								type="monotone"
								dataKey="vapourTemp"
								yAxisId="temp"
								stroke={series[2].color}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 3, strokeWidth: 2, stroke: series[2].color }}
							>
								<LabelList
									dataKey="vapourTemp"
									position="top"
									offset={8}
									style={{
										fontSize: 10,
										fill: series[2].color,
										fontWeight: 500,
									}}
									formatter={(value: number) => (value ? value.toFixed(1) : "")}
								/>
							</Line>
						)}
						{visibility.liquidTemp && (
							<Line
								type="monotone"
								dataKey="liquidTemp"
								yAxisId="temp"
								stroke={series[3].color}
								strokeWidth={2}
								dot={false}
								activeDot={{ r: 3, strokeWidth: 2, stroke: series[3].color }}
							>
								<LabelList
									dataKey="liquidTemp"
									position="bottom"
									offset={8}
									style={{
										fontSize: 10,
										fill: series[3].color,
										fontWeight: 500,
									}}
									formatter={(value: number) => (value ? value.toFixed(1) : "")}
								/>
							</Line>
						)}
					</ComposedChart>
				</ResponsiveContainer>
			</div>
		</div>
	);
}
function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}
function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return {};
}
const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(LNGConsumptionChart);
